import { useState } from "react";
import { Users, UserCheck, UserX, UserMinus, Stethoscope, HeartPulse, Shield } from "lucide-react";

// Mock data for staff
const MOCK_STAFF = [
  { id: "d1", name: "Dr. Sharma", role: "Doctor", department: "Cardiology", status: "present", shift: "Morning (08:00 - 16:00)" },
  { id: "d2", name: "Dr. Verma", role: "Doctor", department: "Neurology", status: "absent", shift: "Morning (08:00 - 16:00)" },
  { id: "d3", name: "Dr. Iyer", role: "Doctor", department: "Emergency", status: "present", shift: "Morning (08:00 - 16:00)" },
  { id: "d4", name: "Dr. Khan", role: "Doctor", department: "Orthopedics", status: "on_leave", shift: "Off" },
  { id: "n1", name: "Nurse Anjali", role: "Nurse", department: "ICU", status: "present", shift: "Morning (08:00 - 16:00)" },
  { id: "n2", name: "Nurse Priya", role: "Nurse", department: "Emergency", status: "present", shift: "Morning (08:00 - 16:00)" },
  { id: "n3", name: "Nurse Rahul", role: "Nurse", department: "General Ward", status: "absent", shift: "Morning (08:00 - 16:00)" },
  { id: "n4", name: "Nurse Meena", role: "Nurse", department: "Pediatrics", status: "present", shift: "Morning (08:00 - 16:00)" },
  { id: "s1", name: "Amit Singh", role: "Support", department: "Housekeeping", status: "present", shift: "Morning (08:00 - 16:00)" },
  { id: "s2", name: "Sunil", role: "Support", department: "Security", status: "present", shift: "Morning (08:00 - 16:00)" },
];

export function StaffAvailability() {
  const [activeTab, setActiveTab] = useState<"all" | "doctor" | "nurse" | "support">("all");

  const filteredStaff = MOCK_STAFF.filter(s => activeTab === "all" || s.role.toLowerCase() === activeTab);
  
  const presentCount = filteredStaff.filter(s => s.status === "present").length;
  const absentCount = filteredStaff.filter(s => s.status === "absent").length;
  const leaveCount = filteredStaff.filter(s => s.status === "on_leave").length;
  const totalCount = filteredStaff.length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "present": return "text-emerald-400 bg-emerald-400/10 border-emerald-400/20";
      case "absent": return "text-red-400 bg-red-400/10 border-red-400/20";
      case "on_leave": return "text-amber-400 bg-amber-400/10 border-amber-400/20";
      default: return "text-gray-400 bg-gray-400/10 border-gray-400/20";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "present": return <UserCheck className="w-3.5 h-3.5" />;
      case "absent": return <UserX className="w-3.5 h-3.5" />;
      case "on_leave": return <UserMinus className="w-3.5 h-3.5" />;
      default: return null;
    }
  };
  
  const getRoleIcon = (role: string) => {
    switch (role) {
      case "Doctor": return <Stethoscope className="w-4 h-4 text-blue-400" />;
      case "Nurse": return <HeartPulse className="w-4 h-4 text-pink-400" />;
      case "Support": return <Shield className="w-4 h-4 text-teal-400" />;
      default: return <Users className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="bg-[#071428]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mt-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-400" />
            Staff Attendance & Availability
          </h3>
          <p className="text-gray-400 text-sm">Real-time tracking of doctors, nurses, and support staff</p>
        </div>
        
        {/* Tabs */}
        <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
          {(["all", "doctor", "nurse", "support"] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${
                activeTab === tab ? "bg-indigo-500/20 text-indigo-400 shadow-lg" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white/5 rounded-xl p-4 border border-white/5 flex flex-col justify-center">
          <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">Total {activeTab === "all" ? "Staff" : activeTab + "s"}</div>
          <div className="text-2xl font-bold text-white">{totalCount}</div>
        </div>
        <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20 flex flex-col justify-center">
          <div className="text-emerald-500 text-xs uppercase tracking-wider mb-1 flex items-center gap-1"><UserCheck className="w-3 h-3"/> Present</div>
          <div className="text-2xl font-bold text-emerald-400">{presentCount}</div>
        </div>
        <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20 flex flex-col justify-center">
          <div className="text-red-500 text-xs uppercase tracking-wider mb-1 flex items-center gap-1"><UserX className="w-3 h-3"/> Absent</div>
          <div className="text-2xl font-bold text-red-400">{absentCount}</div>
        </div>
        <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20 flex flex-col justify-center">
          <div className="text-amber-500 text-xs uppercase tracking-wider mb-1 flex items-center gap-1"><UserMinus className="w-3 h-3"/> On Leave</div>
          <div className="text-2xl font-bold text-amber-400">{leaveCount}</div>
        </div>
      </div>

      {/* Staff Roster Table */}
      <div className="overflow-x-auto rounded-xl border border-white/10 bg-black/20">
        <table className="w-full text-left text-sm whitespace-nowrap">
          <thead className="bg-white/5 text-gray-400 uppercase tracking-wider text-[11px]">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Department</th>
              <th className="px-4 py-3 font-medium">Shift</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filteredStaff.map(staff => (
              <tr key={staff.id} className="hover:bg-white/5 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0 text-indigo-400 font-bold text-xs">
                      {staff.name.charAt(0)}
                    </div>
                    <span className="text-white font-medium">{staff.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-300">
                  <div className="flex items-center gap-1.5">
                    {getRoleIcon(staff.role)}
                    {staff.role}
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-400">{staff.department}</td>
                <td className="px-4 py-3 text-gray-400 font-mono text-xs">{staff.shift}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getStatusColor(staff.status)}`}>
                    {getStatusIcon(staff.status)}
                    {staff.status.replace("_", " ")}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
